import { PermitField, PermitStatus, FieldMetadata } from "types";
import {
  formatLabel,
  formatPhoneNumber,
  formatShortDateTime,
  isValidEmail,
  isValidPhone,
  isValidStateOrStateCode,
  STATES_AND_PROVINCES,
} from "utils";
import dayjs from "dayjs";
import { capitalize } from "@mui/material";

const getPermitFieldMetadata = (
  colorList: string[],
): Record<PermitField, FieldMetadata<PermitField>> => {
  return {
    "permit.permitStatus": {
      field: "permit.permitStatus",
      required: true,
      type: "enum",
      category: "entity",
      specialOptions: [
        {
          value: "Generate Data",
          label: "Generate Data",
        },
      ],
      enumValues: Object.values(PermitStatus).map((value) => value || ""),
      formatter: (value: string | undefined) => {
        if (!value) {
          return "";
        }
        return formatLabel(value) || "";
      },
      validator: (value: string | undefined) => {
        if (!value) {
          return false;
        }
        const formattedValue = value.replace(/ /g, "_").toLowerCase();
        return Object.values(PermitStatus).includes(
          formattedValue as PermitStatus,
        );
      },
      alerts: [
        {
          severity: "info",
          title: "permit status",
          disableClose: false,
          paragraphs: [
            "alerts.fields.status.info.p1",
            "alerts.fields.status.info.p2",
          ],
        },
        {
          severity: "error",
          title: "required",
          disableClose: false,
          paragraphs: [
            "alerts.fields.status.required.p1",
            "alerts.fields.status.required.p2",
            "alerts.fields.status.required.p3",
          ],
        },
      ],
    },
    "permit.startTime": {
      field: "permit.startTime",
      required: true,
      type: "date",
      category: "entity",
      formatter: (value: string | undefined) => {
        if (!value) {
          return "";
        }
        return formatShortDateTime(dayjs(value).toDate());
      },
      validator: (value: string | undefined) => {
        if (!value) {
          return false;
        }
        return dayjs(value).isValid();
      },
      specialOptions: [
        {
          value: "Generate Data",
          label: "Generate Data",
        },
      ],
      alerts: [
        {
          severity: "info",
          title: "start time",
          disableClose: false,
          paragraphs: [
            "alerts.fields.startTime.info.p1",
            "alerts.fields.startTime.info.p2",
          ],
        },
        {
          severity: "error",
          title: "required",
          disableClose: false,
          paragraphs: [
            "alerts.fields.startTime.required.p1",
            "alerts.fields.startTime.required.p2",
          ],
        },
      ],
    },
    "permit.endTime": {
      field: "permit.endTime",
      required: true,
      type: "date",
      category: "entity",
      formatter: (value: string | undefined) => {
        if (!value) {
          return "";
        }
        return formatShortDateTime(dayjs(value).toDate());
      },
      validator: (value: string | undefined) => {
        if (!value) {
          return false;
        }
        return dayjs(value).isValid();
      },
      specialOptions: [
        {
          value: "Generate Data",
          label: "Generate Data",
        },
      ],
      alerts: [
        {
          severity: "info",
          title: "end time",
          disableClose: false,
          paragraphs: [
            "alerts.fields.endTime.info.p1",
            "alerts.fields.endTime.info.p2",
          ],
        },
        {
          severity: "error",
          title: "required",
          disableClose: false,
          paragraphs: [
            "alerts.fields.endTime.required.p1",
            "alerts.fields.endTime.required.p2",
          ],
        },
      ],
    },
    "permit.vehicle.make": {
      field: "permit.vehicle.make",
      required: false,
      type: "string",
      category: "vehicle",
      validator: () => true,
      formatter: (value: string | undefined) => {
        if (!value) {
          return "";
        }
        return value.toUpperCase();
      },
      specialOptions: [
        {
          value: "Smart Map Vehicle",
          label: "Smart Map Vehicle",
        },
        {
          value: "Leave Blank",
          label: "Leave Blank",
        },
      ],
      alerts: [
        {
          severity: "info",
          disableClose: false,
          title: "vehicle make",
          paragraphs: [
            "alerts.fields.make.info.p1",
            "alerts.fields.make.info.p2",
          ],
        },
        {
          severity: "success",
          title: "optional",
          disableClose: false,
          paragraphs: ["alerts.fields.make.optional.p1"],
        },
      ],
    },
    "permit.vehicle.model": {
      field: "permit.vehicle.model",
      required: false,
      type: "string",
      category: "vehicle",
      validator: () => true,
      formatter: (value: string | undefined) => {
        if (!value) {
          return "";
        }
        return value.toUpperCase();
      },
      specialOptions: [
        {
          value: "Smart Map Vehicle",
          label: "Smart Map Vehicle",
        },
        {
          value: "Leave Blank",
          label: "Leave Blank",
        },
      ],
      alerts: [
        {
          severity: "info",
          disableClose: false,
          title: "vehicle model",
          paragraphs: [
            "alerts.fields.model.info.p1",
            "alerts.fields.model.info.p2",
          ],
        },
        {
          severity: "success",
          title: "optional",
          disableClose: false,
          paragraphs: ["alerts.fields.model.optional.p1"],
        },
      ],
    },
    "permit.vehicle.year": {
      field: "permit.vehicle.year",
      required: false,
      type: "string",
      category: "vehicle",
      validator: (value: string | undefined) => {
        if (!value) {
          return true;
        }
        return value.length === 4 && !isNaN(parseInt(value));
      },
      specialOptions: [
        {
          value: "Smart Map Vehicle",
          label: "Smart Map Vehicle",
        },
        {
          value: "Leave Blank",
          label: "Leave Blank",
        },
      ],
      alerts: [
        {
          severity: "info",
          title: "vehicle year",
          disableClose: false,
          paragraphs: ["alerts.fields.year.info.p1"],
        },
        {
          severity: "success",
          title: "optional",
          disableClose: false,
          paragraphs: ["alerts.fields.year.optional.p1"],
        },
      ],
    },
    "permit.vehicle.color": {
      field: "permit.vehicle.color",
      required: false,
      type: "enum",
      category: "vehicle",
      enumValues: colorList,
      validator: (value: string | undefined) => {
        if (!value) {
          return true;
        }
        return colorList.includes(value.toLowerCase());
      },
      formatter: (value: string | undefined) => {
        if (!value) {
          return "";
        }
        return capitalize(value);
      },
      specialOptions: [
        {
          value: "Smart Map Vehicle",
          label: "Smart Map Vehicle",
        },
        {
          value: "Leave Blank",
          label: "Leave Blank",
        },
      ],
      alerts: [
        {
          severity: "info",
          title: "vehicle color",
          disableClose: false,
          paragraphs: [
            "alerts.fields.color.info.p1",
            "alerts.fields.color.info.p2",
          ],
        },
        {
          severity: "success",
          title: "optional",
          disableClose: false,
          paragraphs: ["alerts.fields.color.optional.p1"],
        },
      ],
    },
    "permit.vehicle.vin": {
      field: "permit.vehicle.vin",
      required: false,
      type: "string",
      category: "vehicle",
      validator: (value: string | undefined) => {
        if (!value) {
          return true;
        }
        return value.length > 10;
      },
      formatter: (value: string | undefined) => {
        if (!value) {
          return "";
        }
        return value.toUpperCase();
      },
      specialOptions: [
        {
          value: "Leave Blank",
          label: "Leave Blank",
        },
      ],
      alerts: [
        {
          severity: "info",
          title: "vehicle vin",
          disableClose: false,
          paragraphs: ["alerts.fields.vin.info.p1"],
        },
        {
          severity: "success",
          title: "optional",
          disableClose: false,
          paragraphs: ["alerts.fields.vin.optional.p1"],
        },
      ],
    },
    "permit.vehicle.plate.number": {
      field: "permit.vehicle.plate.number",
      required: false,
      type: "string",
      category: "vehicle",
      formatter: (value: string | undefined) => {
        if (!value) {
          return "";
        }
        return value.toUpperCase();
      },
      validator: () => true,
      specialOptions: [
        {
          value: "Leave Blank",
          label: "Leave Blank",
        },
      ],
      alerts: [
        {
          severity: "info",
          disableClose: false,
          title: "vehicle plate number",
          paragraphs: [
            "alerts.fields.plate_number.info.p1",
            "alerts.fields.plate_number.info.p2",
          ],
        },
        {
          severity: "success",
          title: "optional",
          disableClose: false,
          paragraphs: ["alerts.fields.plate_number.optional.p1"],
        },
      ],
    },
    "permit.vehicle.plate.state": {
      field: "permit.vehicle.plate.state",
      required: false,
      type: "enum",
      category: "vehicle",
      enumValues: STATES_AND_PROVINCES.map((state) => state.stateCode),
      validator: (value: string | undefined) => {
        if (!value) {
          return true;
        }
        return isValidStateOrStateCode(value);
      },
      formatter: (value: string | undefined) => {
        if (!value) {
          return "";
        }
        if (value?.length > 3) {
          const state = STATES_AND_PROVINCES.find(
            (state) => state.label.toLowerCase() === value.toLowerCase(),
          );
          return state ? state.stateCode : "Invalid State";
        } else {
          const stateCode = STATES_AND_PROVINCES.find(
            (state) => state.stateCode === value,
          );
          return stateCode ? stateCode.label : "Invalid State";
        }
      },
      specialOptions: [
        {
          value: "Leave Blank",
          label: "Leave Blank",
        },
      ],
      alerts: [
        {
          severity: "info",
          title: "vehicle plate state",
          paragraphs: ["alerts.fields.plate_state.info.p1"],
        },
        {
          severity: "success",
          title: "optional",
          disableClose: false,
          paragraphs: ["alerts.fields.plate_state.optional.p1"],
        },
      ],
    },
    "permit.contact.name": {
      field: "permit.contact.name",
      required: true,
      type: "string",
      category: "contact",
      validator: (value: string | undefined) => {
        if (!value) {
          return false;
        }
        return true;
      },
      specialOptions: [
        {
          value: "Generate Data",
          label: "Generate Data",
        },
        {
          value: "Combine Fields",
          label: "Combine Fields",
        },
      ],
      alerts: [
        {
          severity: "info",
          title: "contact name",
          disableClose: false,
          paragraphs: ["alerts.fields.contact_name.info.p1"],
        },
        {
          severity: "error",
          title: "required",
          disableClose: false,
          paragraphs: [
            "alerts.fields.contact_name.required.p1",
            "alerts.fields.contact_name.required.p2",
          ],
        },
      ],
    },
    "permit.contact.location.street": {
      field: "permit.contact.location.street",
      required: false,
      type: "string",
      category: "contact",
      validator: () => true,
      specialOptions: [
        {
          value: "Autofill Address",
          label: "Autofill Address",
        },
        {
          value: "Leave Blank",
          label: "Leave Blank",
        },
      ],
      alerts: [
        {
          severity: "info",
          title: "street",
          paragraphs: ["alerts.fields.street.info.p1"],
        },
        {
          severity: "success",
          title: "optional",
          disableClose: false,
          paragraphs: [
            "alerts.fields.street.optional.p1",
            "alerts.fields.street.optional.p2",
          ],
        },
      ],
    },
    "permit.contact.location.city": {
      field: "permit.contact.location.city",
      required: false,
      type: "string",
      category: "contact",
      validator: () => true,
      specialOptions: [
        {
          value: "Autofill Address",
          label: "Autofill Address",
        },
        {
          value: "Leave Blank",
          label: "Leave Blank",
        },
      ],
      alerts: [
        {
          severity: "info",
          title: "city",
          paragraphs: ["alerts.fields.city.info.p1"],
        },
        {
          severity: "success",
          title: "optional",
          disableClose: false,
          paragraphs: [
            "alerts.fields.city.optional.p1",
            "alerts.fields.city.optional.p2",
          ],
        },
      ],
    },
    "permit.contact.location.stateCode": {
      field: "permit.contact.location.stateCode",
      required: false,
      type: "string",
      category: "contact",
      validator: (value: string | undefined) => {
        if (!value) {
          return true;
        }
        return isValidStateOrStateCode(value);
      },
      formatter: (value: string | undefined) => {
        if (!value) {
          return "";
        }
        if (value.length > 3) {
          const state = STATES_AND_PROVINCES.find(
            (state) => state.stateCode === value,
          );
          return state ? state.stateCode : value;
        }
        return value;
      },
      specialOptions: [
        {
          value: "Autofill Address",
          label: "Autofill Address",
        },
        {
          value: "Leave Blank",
          label: "Leave Blank",
        },
      ],
      alerts: [
        {
          severity: "info",
          title: "state",
          paragraphs: ["alerts.fields.stateCode.info.p1"],
        },
        {
          severity: "success",
          title: "optional",
          disableClose: false,
          paragraphs: [
            "alerts.fields.stateCode.optional.p1",
            "alerts.fields.stateCode.optional.p2",
          ],
        },
      ],
    },
    "permit.contact.location.postalCode": {
      field: "permit.contact.location.postalCode",
      required: false,
      type: "string",
      category: "contact",
      validator: (value: string | undefined) => {
        if (!value) {
          return true;
        }
        return value.length > 4;
      },
      specialOptions: [
        {
          value: "Autofill Address",
          label: "Autofill Address",
        },
        {
          value: "Leave Blank",
          label: "Leave Blank",
        },
      ],
      alerts: [
        {
          severity: "info",
          title: "postal code",
          paragraphs: ["alerts.fields.postalCode.info.p1"],
        },
        {
          severity: "success",
          title: "optional",
          disableClose: false,
          paragraphs: [
            "alerts.fields.postalCode.optional.p1",
            "alerts.fields.postalCode.optional.p2",
          ],
        },
      ],
    },
    "permit.contact.location.unit": {
      field: "permit.contact.location.unit",
      required: false,
      type: "string",
      category: "contact",
      validator: () => true,
      specialOptions: [
        {
          value: "Leave Blank",
          label: "Leave Blank",
        },
      ],
      alerts: [
        {
          severity: "info",
          title: "unit",
          paragraphs: ["alerts.fields.unit.info.p1"],
        },
        {
          severity: "success",
          title: "optional",
          disableClose: false,
          paragraphs: ["alerts.fields.unit.optional.p1"],
        },
      ],
    },
    "permit.contact.primaryEmail": {
      field: "permit.contact.primaryEmail",
      required: false,
      type: "string",
      category: "contact",
      validator: (value: string | undefined) => {
        if (!value) {
          return true;
        }
        return isValidEmail(value);
      },
      specialOptions: [
        {
          value: "Leave Blank",
          label: "Leave Blank",
        },
      ],
      alerts: [
        {
          severity: "info",
          title: "primary email",
          paragraphs: [
            "alerts.fields.primaryEmail.info.p1",
            "alerts.fields.primaryEmail.info.p2",
          ],
        },
        {
          severity: "success",
          title: "optional",
          disableClose: false,
          paragraphs: [
            "alerts.fields.primaryEmail.optional.p1",
            "alerts.fields.primaryEmail.optional.p2",
          ],
        },
      ],
    },
    "permit.contact.primaryPhone": {
      field: "permit.contact.primaryPhone",
      required: false,
      type: "string",
      category: "contact",
      validator: (value: string | undefined) => {
        if (!value) {
          return true;
        }
        return isValidPhone(value);
      },
      formatter: (value: string | undefined) => {
        if (!value) {
          return "";
        }
        return formatPhoneNumber(value.replace(/\D/g, ""));
      },
      specialOptions: [
        {
          value: "Leave Blank",
          label: "Leave Blank",
        },
      ],
      alerts: [
        {
          severity: "info",
          title: "primary phone",
          paragraphs: [
            "alerts.fields.primaryPhone.info.p1",
            "alerts.fields.primaryPhone.info.p2",
          ],
        },
        {
          severity: "success",
          title: "optional",
          disableClose: false,
          paragraphs: [
            "alerts.fields.primaryPhone.optional.p1",
            "alerts.fields.primaryPhone.optional.p2",
          ],
        },
      ],
    },
  };
};

export default getPermitFieldMetadata;
